import React, { useState, useEffect } from "react";
import { Download, ArrowRight, Smartphone, Loader2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { BACKEND_API_URL } from "@/utils/api";

const DownloadPage = () => {
  const [versionInfo, setVersionInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchVersionInfo = async () => {
      try {
        const response = await fetch(`${BACKEND_API_URL}/api/app-version/`);
        if (!response.ok) throw new Error("Failed to fetch version info");
        const data = await response.json();
        setVersionInfo(data);
      } catch (err) {
        setError(err.message);
        // Fallback to hardcoded values if API fails
        setVersionInfo({
          version_name: "1.0.0",
          download_url: "https://drive.google.com/uc?export=download&id=1afYhiS7NqCBwyOJBElhqITgz0RCyC_bt",
        });
      } finally {
        setLoading(false);
      }
    };
    fetchVersionInfo();
  }, []);

  const appFileName = "app-release.apk";
  const appVersion = versionInfo?.version_name || "1.0.0";
  const downloadUrl = versionInfo?.download_url || "";

  const handleDownload = () => {
    if (downloadUrl) {
      window.open(downloadUrl, '_blank');
    }
  };

  return (
    <div style={styles.container}>
      {/* Geometric background shapes */}
      <div style={styles.bgShape1} />
      <div style={styles.bgShape2} />
      <div style={styles.bgShape3} />

      {/* Main content */}
      <div style={styles.content}>
        <div style={styles.wrapper}>
          {/* Logo and title */}
          <div style={styles.header}>
            <div style={styles.iconBox}>
              <img src="/letter-m.png" alt="Morphle" style={styles.icon} />
            </div>
            <h1 style={styles.title}>
              Morphle OneMap
            </h1>
            <p style={styles.subtitle}>
              Your mobile companion for seamless task management
            </p>
          </div>

          {/* Download card */}
          <div style={styles.card}>
            {/* Version badge */}
            <div style={styles.badgeContainer}>
              <span style={styles.badge}>
                <span style={styles.badgeDot} />
                {loading ? "Loading..." : `Version ${appVersion}`}
              </span>
            </div>

            {/* Download button */}
            <button
              onClick={handleDownload}
              style={{
                ...styles.downloadButton,
                opacity: loading ? 0.7 : 1,
                cursor: loading ? "wait" : "pointer",
              }}
              disabled={loading}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.transform = "translateY(-2px)";
                  e.target.style.boxShadow = "0 8px 24px rgba(80, 70, 229, 0.4)";
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "0 4px 12px rgba(80, 70, 229, 0.3)";
              }}
            >
              {loading ? (
                <Loader2 style={{ width: 20, height: 20, animation: "spin 1s linear infinite" }} />
              ) : (
                <Download style={{ width: 20, height: 20 }} />
              )}
              <span>Download for Android</span>
              <ArrowRight style={{ width: 16, height: 16, marginLeft: 8 }} />
            </button>

            {/* File info */}
            <p style={styles.fileInfo}>
              {appFileName} • Android 8.0+
            </p>

            {/* QR Code Section */}
            <div style={styles.qrSection}>
              <div style={styles.qrDivider}>
                <span style={styles.qrDividerLine} />
                <span style={styles.qrDividerText}>or scan to download</span>
                <span style={styles.qrDividerLine} />
              </div>

              <div style={styles.qrContainer}>
                <div style={styles.qrWrapper}>
                  {downloadUrl && (
                    <QRCodeSVG
                      value={downloadUrl}
                      size={160}
                      level="H"
                      includeMargin={false}
                      bgColor="#ffffff"
                      fgColor="#1a1a1f"
                    />
                  )}
                </div>
                <div style={styles.qrLabel}>
                  <Smartphone style={{ width: 16, height: 16 }} />
                  <span>Point your camera at the QR code</span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer note */}
          <p style={styles.footerNote}>
            Having trouble?{" "}
            <a
              href="mailto:siddhesh@morphle.in?subject=Morphle%20Task%20Logger%20Support"
              style={styles.supportLink}
            >
              Contact support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#ffffff",
    display: "flex",
    flexDirection: "column",
    position: "relative",
    overflow: "hidden",
  },
  // Sharp geometric background shapes
  bgShape1: {
    position: "absolute",
    top: -100,
    right: -100,
    width: 400,
    height: 400,
    backgroundColor: "#5046e5",
    opacity: 0.05,
    transform: "rotate(45deg)",
  },
  bgShape2: {
    position: "absolute",
    bottom: -150,
    left: -150,
    width: 500,
    height: 500,
    backgroundColor: "#fbbf24",
    opacity: 0.05,
    transform: "rotate(15deg)",
  },
  bgShape3: {
    position: "absolute",
    top: "40%",
    left: "60%",
    width: 200,
    height: 200,
    border: "2px solid #e5e5e8",
    opacity: 0.3,
    transform: "rotate(30deg)",
  },
  content: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    position: "relative",
    zIndex: 10,
  },
  wrapper: {
    maxWidth: 480,
    width: "100%",
  },
  header: {
    textAlign: "center",
    marginBottom: 40,
  },
  iconBox: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 88,
    height: 88,
    marginBottom: 24,
  },
  icon: {
    width: 56,
    height: 56,
    objectFit: "contain",
  },
  title: {
    fontSize: 36,
    fontWeight: 700,
    color: "#1a1a1f",
    marginBottom: 12,
    letterSpacing: "-0.02em",
    fontFamily: "'Wotfard', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif",
  },
  subtitle: {
    fontSize: 16,
    color: "#71717a",
    margin: 0,
    fontFamily: "'Wotfard', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif",
  },
  card: {
    backgroundColor: "#ffffff",
    border: "1px solid #e5e5e8",
    padding: 32,
    // Sharp corners - no border-radius
  },
  badgeContainer: {
    display: "flex",
    justifyContent: "center",
    marginBottom: 24,
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 16px",
    backgroundColor: "#f4f4f5",
    color: "#5046e5",
    fontSize: 14,
    fontWeight: 500,
    fontFamily: "'Wotfard', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif",
    // Sharp - no border-radius
  },
  badgeDot: {
    width: 8,
    height: 8,
    backgroundColor: "#22c55e",
    // Keep dot square for ultra-sharp aesthetic
  },
  downloadButton: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    backgroundColor: "#5046e5",
    color: "#f8f8fc",
    fontWeight: 600,
    fontSize: 16,
    padding: "16px 24px",
    border: "none",
    cursor: "pointer",
    transition: "all 0.2s ease",
    boxShadow: "0 4px 12px rgba(80, 70, 229, 0.3)",
    fontFamily: "'Wotfard', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif",
    // Sharp - no border-radius
  },
  fileInfo: {
    textAlign: "center",
    fontSize: 13,
    color: "#71717a",
    marginTop: 16,
    marginBottom: 0,
    fontFamily: "'Wotfard', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif",
  },
  qrSection: {
    marginTop: 28,
  },
  qrDivider: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    marginBottom: 24,
  },
  qrDividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: "#e5e5e8",
  },
  qrDividerText: {
    fontSize: 13,
    color: "#71717a",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    fontFamily: "'Wotfard', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif",
  },
  qrContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 16,
  },
  qrWrapper: {
    padding: 16,
    backgroundColor: "#ffffff",
    border: "2px solid #e5e5e8",
    // Sharp corners maintained
  },
  qrLabel: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    color: "#71717a",
    fontFamily: "'Wotfard', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif",
  },
  footerNote: {
    textAlign: "center",
    fontSize: 14,
    color: "#71717a",
    marginTop: 32,
    fontFamily: "'Wotfard', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif",
  },
  supportLink: {
    color: "#5046e5",
    textDecoration: "none",
  },
};

export default DownloadPage;
